import { useEffect, useRef } from 'react';

/**
 * Global keyboard shortcut hook.
 *
 * Supports:
 *   - Single keys: "j", "k", "/"
 *   - Modifier combos: "mod+k" (⌘K / Ctrl+K), "shift+?", "mod+shift+a"
 *   - Sequences (vim-style): "g d", "g i", "g s"  (two keys within 800ms)
 *
 * `map` = { "mod+k": () => {...}, "j": () => {...}, "g d": () => {...} }
 *
 * By default the hook ignores keystrokes while the user is typing in an
 * input / textarea / contenteditable. Pass `{ captureInInputs: true }`
 * to override, or prefix a binding with "!" (e.g. "!mod+k") to fire
 * regardless of focus.
 *
 * `enabled` flips the whole listener off.
 */
export default function useKeyboardShortcuts(map, { enabled = true } = {}) {
  const mapRef = useRef(map);
  mapRef.current = map;

  const pendingRef = useRef(null); // for sequences: { key, at }

  useEffect(() => {
    if (!enabled) return undefined;

    const handler = (e) => {
      const m = mapRef.current;
      if (!m) return;
      const target = e.target;
      const tag = target?.tagName;
      const isTyping =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        target?.isContentEditable;

      const key = (e.key || '').toLowerCase();
      if (!key || key === 'shift' || key === 'control' || key === 'meta' || key === 'alt') return;

      // Build current combo signature
      const mod = e.metaKey || e.ctrlKey;
      const shift = e.shiftKey;
      const alt = e.altKey;
      const parts = [];
      if (mod) parts.push('mod');
      if (alt) parts.push('alt');
      if (shift && key.length > 1) parts.push('shift'); // explicit shift for non-char keys
      parts.push(key === ' ' ? 'space' : key);
      const combo = parts.join('+');

      // Try: force-mode "!combo", then plain "combo"
      const forced = m[`!${combo}`];
      const normal = m[combo];
      const handlerFn = forced || (!isTyping ? normal : null);

      if (handlerFn) {
        e.preventDefault();
        pendingRef.current = null;
        handlerFn(e);
        return;
      }

      // Sequence handling — only single-character keys without modifiers
      if (!mod && !alt && !shift && key.length === 1 && !isTyping) {
        const now = Date.now();
        const pending = pendingRef.current;
        if (pending && now - pending.at < 800) {
          const seq = `${pending.key} ${key}`;
          const seqFn = m[seq];
          if (seqFn) {
            e.preventDefault();
            pendingRef.current = null;
            seqFn(e);
            return;
          }
          pendingRef.current = null;
        }
        // If any binding starts with this key + space, treat as sequence start
        const startsSequence = Object.keys(m).some((k) => k.startsWith(`${key} `));
        if (startsSequence) {
          pendingRef.current = { key, at: now };
          return;
        }
      }

      // Single key without modifiers (e.g. "j", "k", "/", "?")
      if (!mod && !alt && key.length === 1 && !isTyping) {
        const direct = m[key] || (shift && m[`shift+${key}`]);
        if (direct) {
          e.preventDefault();
          direct(e);
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled]);
}

/** Human-readable shortcut label for hints. */
export function prettyShortcut(combo) {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  return combo
    .replace('mod', isMac ? '⌘' : 'Ctrl')
    .replace('shift', '⇧')
    .replace('alt', isMac ? '⌥' : 'Alt')
    .split('+')
    .map((p) => p.length === 1 ? p.toUpperCase() : p)
    .join(isMac ? '' : '+');
}
